import type { Company } from "@/types/company.type";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Building2,
  Calendar,
  Check,
  ChevronsUpDown,
  Filter,
  Loader2,
  Search,
  X,
} from "lucide-react";

import { Button } from "../ui/button";
import { Calendar as CalendarComponent } from "../ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { cn } from "@/lib/utils";

export type FilterType = "all" | "day" | "month" | "period";

export type AllSessionsFiltersBarProps = {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;

  filterType: FilterType;
  onFilterTypeChange: (value: FilterType) => void;

  selectedDay?: Date;
  onSelectedDayChange: (day: Date | undefined) => void;
  dayPopoverOpen: boolean;
  onDayPopoverOpenChange: (open: boolean) => void;

  selectedMonth?: Date;
  onSelectedMonthChange: (month: Date | undefined) => void;
  monthPopoverOpen: boolean;
  onMonthPopoverOpenChange: (open: boolean) => void;

  periodStart?: Date;
  periodEnd?: Date;
  onPeriodStartChange: (d: Date | undefined) => void;
  onPeriodEndChange: (d: Date | undefined) => void;
  periodStartPopoverOpen: boolean;
  onPeriodStartPopoverOpenChange: (open: boolean) => void;
  periodEndPopoverOpen: boolean;
  onPeriodEndPopoverOpenChange: (open: boolean) => void;

  isSuperuser: boolean;
  companies: Company[];
  isFetchingCompanies: boolean;
  companyFilter: string;
  onCompanyFilterChange: (value: string) => void;
  companyPopoverOpen: boolean;
  onCompanyPopoverOpenChange: (open: boolean) => void;
  companySearch: string;
  onCompanySearchChange: (value: string) => void;
  selectedCompanyLabel: string;

  showActiveSessions: boolean;
  onShowActiveSessionsChange: (checked: boolean) => void;

  hasActiveFilters: boolean;
  onResetFilters: () => void;
};

export function AllSessionsFiltersBar(props: AllSessionsFiltersBarProps) {
  const {
    searchQuery,
    onSearchQueryChange,
    filterType,
    onFilterTypeChange,
    selectedDay,
    onSelectedDayChange,
    dayPopoverOpen,
    onDayPopoverOpenChange,
    selectedMonth,
    onSelectedMonthChange,
    monthPopoverOpen,
    onMonthPopoverOpenChange,
    periodStart,
    periodEnd,
    onPeriodStartChange,
    onPeriodEndChange,
    periodStartPopoverOpen,
    onPeriodStartPopoverOpenChange,
    periodEndPopoverOpen,
    onPeriodEndPopoverOpenChange,
    isSuperuser,
    companies,
    isFetchingCompanies,
    companyFilter,
    onCompanyFilterChange,
    companyPopoverOpen,
    onCompanyPopoverOpenChange,
    companySearch,
    onCompanySearchChange,
    selectedCompanyLabel,
    showActiveSessions,
    onShowActiveSessionsChange,
    hasActiveFilters,
    onResetFilters,
  } = props;

  return (
    <div className="mb-4 sm:mb-6 flex flex-col gap-3 rounded-lg border p-4 sm:gap-4 md:flex-row md:flex-nowrap md:items-center">
      <div className="relative min-w-0 flex-1 md:max-w-xs">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Rechercher par nom ou PIN..."
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          className="w-full pl-10"
        />
      </div>

      <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
        <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
        <Select
          value={filterType}
          onValueChange={(value) =>
            onFilterTypeChange(value as FilterType)
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filtrer par..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les sessions</SelectItem>
            <SelectItem value="day">Par jour</SelectItem>
            <SelectItem value="month">Par mois</SelectItem>
            <SelectItem value="period">Par période</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex shrink-0 flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center md:flex-nowrap md:items-center sm:gap-3">
        {filterType === "day" && (
          <Popover open={dayPopoverOpen} onOpenChange={onDayPopoverOpenChange}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full gap-2 sm:w-auto">
                <Calendar className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  {selectedDay
                    ? format(selectedDay, "dd/MM/yyyy", { locale: fr })
                    : "Choisir un jour"}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={selectedDay}
                onSelect={(day) => {
                  if (day) {
                    onSelectedDayChange(day);
                    onDayPopoverOpenChange(false);
                  }
                }}
                className={cn("pointer-events-auto p-3")}
              />
            </PopoverContent>
          </Popover>
        )}

        {filterType === "month" && (
          <Popover
            open={monthPopoverOpen}
            onOpenChange={onMonthPopoverOpenChange}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full gap-2 sm:w-auto">
                <Calendar className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  {selectedMonth
                    ? format(selectedMonth, "MMMM yyyy", { locale: fr })
                    : "Choisir un mois"}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={selectedMonth}
                onSelect={(day) => {
                  if (day) {
                    onSelectedMonthChange(
                      new Date(day.getFullYear(), day.getMonth(), 1)
                    );
                    onMonthPopoverOpenChange(false);
                  }
                }}
                className={cn("pointer-events-auto p-3")}
              />
            </PopoverContent>
          </Popover>
        )}

        {filterType === "period" && (
          <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
            <Popover
              open={periodStartPopoverOpen}
              onOpenChange={onPeriodStartPopoverOpenChange}
            >
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full gap-2 sm:w-auto">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {periodStart
                      ? format(periodStart, "dd/MM/yyyy", { locale: fr })
                      : "Date début"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={periodStart}
                  onSelect={(day) => {
                    if (day) {
                      onPeriodStartChange(day);
                      onPeriodStartPopoverOpenChange(false);
                    }
                  }}
                  className={cn("pointer-events-auto p-3")}
                />
              </PopoverContent>
            </Popover>
            <span className="hidden text-muted-foreground sm:inline">→</span>
            <Popover
              open={periodEndPopoverOpen}
              onOpenChange={onPeriodEndPopoverOpenChange}
            >
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full gap-2 sm:w-auto">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {periodEnd
                      ? format(periodEnd, "dd/MM/yyyy", { locale: fr })
                      : "Date fin"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={periodEnd}
                  onSelect={(day) => {
                    if (day) {
                      onPeriodEndChange(day);
                      onPeriodEndPopoverOpenChange(false);
                    }
                  }}
                  className={cn("pointer-events-auto p-3")}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {isSuperuser && (
          <Popover
            open={companyPopoverOpen}
            onOpenChange={(nextOpen) => {
              onCompanyPopoverOpenChange(nextOpen);
              if (!nextOpen) {
                onCompanySearchChange("");
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className={cn(
                  "w-full justify-between sm:w-[250px]",
                  companyFilter === "all" && "text-muted-foreground"
                )}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span className="truncate">{selectedCompanyLabel}</span>
                </div>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <Command>
                <CommandInput
                  placeholder="Rechercher une entreprise..."
                  value={companySearch}
                  onValueChange={onCompanySearchChange}
                />
                <CommandList>
                  {isFetchingCompanies ? (
                    <div className="py-3 text-center text-sm text-muted-foreground">
                      <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                    </div>
                  ) : (
                    <>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          onCompanyFilterChange("all");
                          onCompanyPopoverOpenChange(false);
                          onCompanySearchChange("");
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            companyFilter === "all"
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        Toutes les entreprises
                      </CommandItem>
                      {companies.length === 0 ? (
                        <CommandEmpty>Aucune entreprise trouvée.</CommandEmpty>
                      ) : (
                        companies.map((company) => (
                          <CommandItem
                            key={company.id}
                            value={company.name}
                            onSelect={() => {
                              onCompanyFilterChange(company.id);
                              onCompanyPopoverOpenChange(false);
                              onCompanySearchChange("");
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                companyFilter === company.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            {company.name}
                          </CommandItem>
                        ))
                      )}
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            Sessions en cours
          </span>
          <Switch
            checked={showActiveSessions}
            onCheckedChange={onShowActiveSessionsChange}
          />
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            className="w-full gap-1 sm:w-auto"
          >
            <X className="h-4 w-4" />
            Réinitialiser
          </Button>
        )}

      </div>
    </div>
  );
}
